import React, { useState, useEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import { Help } from '@material-ui/icons';
import { ESnapshotExists, EOrganization, ENotice } from 'lib/types';
import {
  getDeadlineTimeForPaper,
  isNoticeAfterPublicationDeadline
} from 'lib/utils/deadlines';
import { firstNoticePublicationDate } from 'lib/helpers';
import classNames from 'classnames';
import { useInheritedProperty } from 'lib/frontend/hooks/useInheritedProperty';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { Tooltip } from 'lib/components/Tooltip';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import ColumnDatePicker from 'components/ColumnDatePicker';

interface ToggleSwitchProps {
  id: string;
  value: boolean;
  disabled?: boolean;
  onChange: (val: boolean) => unknown;
}

function ToggleSwitch({ id, value, disabled, onChange }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      data-testid={id}
      id={id}
      className={classNames(
        'p-1 flex-shrink-0 group relative rounded-full inline-flex w-12',
        value ? 'bg-blue-650' : 'bg-column-gray-300',
        {
          'justify-end': value,
          'cursor-pointer': !disabled,
          'opacity-25': disabled
        }
      )}
      disabled={disabled}
      onClick={() => {
        if (disabled) {
          return;
        }
        onChange(!value);
      }}
    >
      <span className="sr-only">Use setting</span>
      <span
        aria-hidden="true"
        className={`inline-block h-4 w-4 rounded-full bg-white shadow`}
      ></span>
    </button>
  );
}

type InvoiceFormDueDateProps = {
  newspaper: ESnapshotExists<EOrganization>;
  dueDate?: number;
  setDueDate: (dueDate: number) => void;
  noticeSnap: ESnapshotExists<ENotice>;
  requireUpfrontPayment?: boolean;
  onRequireUpfrontPaymentChange: (requireUpfrontPayment: boolean) => void;
  isBillingDataLoading?: boolean;
  ianaTimezone?: string;
  invoiceOutsideColumn: boolean;
  setInvoiceOutsideColumn: (invoiceOutsideColumn: boolean) => void;
};

function InvoiceFormDueDate({
  newspaper,
  dueDate,
  setDueDate,
  noticeSnap,
  requireUpfrontPayment,
  onRequireUpfrontPaymentChange,
  isBillingDataLoading,
  ianaTimezone,
  invoiceOutsideColumn,
  setInvoiceOutsideColumn
}: InvoiceFormDueDateProps) {
  const [disableRequireUpfrontPayment, setDisableRequireUpfrontPayment] =
    useState(false);
  const [deadline, setDeadline] = useState<Moment>();

  useEffect(() => {
    const {
      deadlines,
      deadlineOverrides = {},
      iana_timezone
    } = newspaper.data();
    if (!deadlines) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        new Error('No deadlines found for newspaper'),
        'Missing deadlines in InvoiceFormDueDate',
        {
          newspaperId: newspaper.id
        }
      );
      return;
    }
    const closestPublicationDate: Date = firstNoticePublicationDate(noticeSnap);
    const deadline = getDeadlineTimeForPaper(
      closestPublicationDate,
      deadlines,
      deadlineOverrides,
      iana_timezone,
      noticeSnap.data(),
      newspaper
    );
    setDeadline(deadline);
  }, [noticeSnap, newspaper]);

  useEffect(() => {
    const checkDeadline = async () => {
      if (deadline) {
        const isAfterDeadline = await isNoticeAfterPublicationDeadline(
          noticeSnap
        );
        if (!isAfterDeadline) {
          setDisableRequireUpfrontPayment(false);
        } else {
          setDisableRequireUpfrontPayment(true);
          onRequireUpfrontPaymentChange(false);
        }
      }
    };

    void checkDeadline();
  }, [deadline]);

  // When the organization has the disableControlUpfrontPayment setting,
  // require upfront payment and invoiceOutsideColumn are mutually exclusive.
  // We use IOC as the "controlling" variable and derive RUP accordingly.
  const disableControlUpfrontPayment = useInheritedProperty(
    newspaper.ref,
    'disableControlUpfrontPayment'
  );

  // In the case where RUP is not an option (after deadline) and the newspaper has
  // the setting which makes IOC and RUP mutally exclusive, they cannot control either
  const isAfterDeadlineAndIOCRUPMutuallyExclusive =
    !requireUpfrontPayment &&
    disableRequireUpfrontPayment &&
    disableControlUpfrontPayment;

  const blockNonIOCInvoices = useInheritedProperty(
    newspaper.ref,
    'blockNonIOCInvoices'
  );

  const forceInvoiceOutsideColumn =
    blockNonIOCInvoices || isAfterDeadlineAndIOCRUPMutuallyExclusive;

  useEffect(() => {
    if (forceInvoiceOutsideColumn) {
      setInvoiceOutsideColumn(true);
    }
  }, [forceInvoiceOutsideColumn]);

  useEffect(() => {
    if (
      disableControlUpfrontPayment &&
      requireUpfrontPayment === invoiceOutsideColumn
    ) {
      setInvoiceOutsideColumn(!requireUpfrontPayment);
    }
  }, [
    disableControlUpfrontPayment,
    requireUpfrontPayment,
    invoiceOutsideColumn
  ]);

  const handleToggleInvoiceOutsideColumn = (val: boolean) => {
    setInvoiceOutsideColumn(val);
    if (disableControlUpfrontPayment && val === requireUpfrontPayment) {
      onRequireUpfrontPaymentChange(!val);
    }
  };

  const handleDueDateChange = (date: Date | undefined) => {
    if (!date || !ianaTimezone) {
      return;
    }

    const selectedDay = moment(date).format('YYYY-MM-DD');

    if (requireUpfrontPayment && deadline) {
      const customDeadline = moment.tz(selectedDay, ianaTimezone).set({
        hour: deadline.hour(),
        minute: deadline.minute()
      });
      setDueDate(customDeadline.toDate().getTime() / 1000);
      return;
    }

    const newDueDate = moment.tz(selectedDay, ianaTimezone).endOf('day');

    setDueDate(newDueDate.toDate().getTime() / 1000 - 2);
  };

  return (
    <div>
      <div className="bg-white shadow overflow-visible sm:rounded-lg mt-5">
        <div className="px-4 py-5 sm:p-0">
          <dl>
            <div className="flex justify-between items-center px-4 pt-5 pb-3 border-b border-gray-300">
              <div className="flex items-center text-sm leading-5 font-bold text-gray-800">
                <div className="mr-2">Require upfront payment?</div>
                <Tooltip
                  helpText={`When enabled, upfront payment will be required such that invoice will have a due date one day prior the deadline`}
                >
                  <Help
                    color="disabled"
                    style={{ width: '1rem', fill: '#2A394A' }}
                  />
                </Tooltip>
              </div>
              <Tooltip
                helpText={
                  disableRequireUpfrontPayment
                    ? `Cannot require upfront payment on this invoice because the ad deadline for this notice has already passed. If the invoice must be prepaid, please reschedule the notice to a future date.`
                    : ``
                }
                position="topLeft"
              >
                <div className="flex mt-1 text-right text-sm leading-5 text-gray-900 sm:mt-0">
                  <ToggleSwitch
                    id="require-upfront-payment"
                    value={!!requireUpfrontPayment}
                    onChange={val => onRequireUpfrontPaymentChange(val)}
                    disabled={
                      isBillingDataLoading ||
                      disableControlUpfrontPayment ||
                      disableRequireUpfrontPayment
                    }
                  />
                </div>
              </Tooltip>
            </div>
            {newspaper.data().allowInvoiceOutsideColumn && (
              <div className="flex justify-between items-center px-4 pt-5 pb-3 border-b border-gray-300">
                <div className="flex items-center text-sm leading-5 font-bold text-gray-800">
                  <div className="mr-2">Invoice outside of Column?</div>
                  <Tooltip
                    helpText={`When enabled, this invoice will be sent outside of the Column platform`}
                  >
                    <Help
                      color="disabled"
                      style={{ width: '1rem', fill: '#2A394A' }}
                    />
                  </Tooltip>
                </div>
                <Tooltip
                  helpText={
                    invoiceOutsideColumn
                      ? `This customer will receive their invoice outside of the Column platform, handled separately by the publisher.`
                      : 'This customer will receive their invoice through the Column platform.'
                  }
                  position="topLeft"
                >
                  <div className="flex mt-1 text-right text-sm leading-5 text-gray-900 sm:mt-0">
                    <ToggleSwitch
                      id="invoice-outside-column"
                      value={!!invoiceOutsideColumn}
                      onChange={handleToggleInvoiceOutsideColumn}
                      disabled={
                        !!isBillingDataLoading || forceInvoiceOutsideColumn
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            )}
            <div className="flex justify-between px-4 pt-5 pb-3">
              <div>
                <dt className="font-bold">Creation date</dt>
                <dd>
                  {moment(new Date())
                    .tz(ianaTimezone || 'America/New_York')
                    .format('MMMM D, YYYY')}
                </dd>
              </div>
              <div>
                <span className="font-bold">Due date</span>
                {isBillingDataLoading || !dueDate ? (
                  <div className="grid h-9 place-items-center">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <ColumnDatePicker
                    format="MMM dd, yyyy"
                    momentFormat="MMMM D, YYYY"
                    placeholderText="Select a date"
                    value={moment(dueDate * 1000)
                      .tz(ianaTimezone || 'America/New_York')
                      .format('YYYY-MM-DD')}
                    onChange={handleDueDateChange}
                    shouldDisableDate={date => {
                      if (!date) {
                        return true;
                      }

                      const now = new Date();
                      if (date.getTime() < now.getTime()) return true;
                      if (
                        requireUpfrontPayment &&
                        deadline &&
                        date.getTime() > deadline.valueOf()
                      ) {
                        return true;
                      }
                      return false;
                    }}
                    disabled={isBillingDataLoading}
                    fullWidth
                  />
                )}
              </div>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

export default InvoiceFormDueDate;
